import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useLoading from "../../hooks/useLoading";
import usePushNotification from "../../hooks/usePushNotification";
import { useRouter } from "next/router";
import { toast } from "sonner";
import { redeployProject } from "../../backend/projects";
import Modal, { ModalContent } from "../Micro/Modal/Modal";
import IconButton from "../Micro/IconButton/IconButton";
import { MdClose } from "react-icons/md";
import Image from "next/image";
import Button from "../Micro/Button/Button";
import { useStoreContext } from "../../context";
import { Actions } from "../../context/reducer";
import { handleRequestError } from "../../backend";
import useIsTeam from "../../hooks/useIsTeam";
import useLogs from "../../hooks/useLogs";
import Skeleton from "../Micro/Skeleton/Skeleton";
import useAbly from "../../hooks/useAbly";
import useChatMessages from "../../hooks/useChatMessages";
import { formatDate, log_class } from "@/helpers";
import {
  ArrowDown,
  Send,
  AlertCircle,
  ChevronDown,
  Settings2,
} from "lucide-react";
import { ServiceType } from "@brimble/models";
import ModalHeaderContent from "./ModalHeaderContent";
import { format } from "date-fns";

const urlPattern = /(https?:\/\/[^\s]+)/g;

const convertUrlsToLinks = (text: string) => {
  const parts = text.split(urlPattern);
  return parts.map((part, index) => {
    if (part.match(urlPattern)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 underline hover:text-blue-300"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

let timerout: any = undefined;

type LogsData = {
  id: string | number | undefined;
  projectId: string | number | undefined;
  title: string | undefined;
  type: string | undefined;
  logName: string | undefined;
  time: any;
};

type LogEntry = {
  content: string;
  id?: string;
};

export enum LogMessageUser {
  User = "user",
  System = "system",
  Assistant = "assistant",
}

type ChatMessage = {
  id: string;
  conversation_id: string;
  role: LogMessageUser;
  content: string;
  created_at: Date;
  log_id: string;
  project_id: string;
  isTemporaryLoading?: boolean;
};

type AIModel = {
  id: string;
  name: string;
  description: string;
};

type AIConfig = {
  maxTokens: number;
  temperature: number;
};

const AI_MODELS: AIModel[] = [
  {
    id: "gpt-4",
    name: "GPT-4",
    description: "Most capable model, best for complex tasks",
  },
  {
    id: "gpt-3.5-turbo",
    name: "GPT-3.5 Turbo",
    description: "Fast and efficient for most tasks",
  },
  {
    id: "claude-3",
    name: "Claude 3",
    description: "Great for analysis and reasoning",
  },
];

const DEFAULT_CONFIG: AIConfig = {
  maxTokens: 1000,
  temperature: 0.7,
};

const LogsModal: React.FC = () => {
  const [timer, setTimer] = useState<number>(0);
  const [timing, setTiming] = useState<boolean>(false);
  const [deployed, setDeployed] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [minimized, setMinimized] = useState<boolean>(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState<boolean>(true);
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const [showAIChat, setShowAIChat] = useState<boolean>(false);
  const [inputMessage, setInputMessage] = useState("");
  const [hasSeenDisclaimer, setHasSeenDisclaimer] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<AIModel>(AI_MODELS[0]);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [config, setConfig] = useState<AIConfig>(DEFAULT_CONFIG);

  const logContainerRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const lastScrollPosition = useRef<number>(0);
  const lastLogLength = useRef<number>(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  const { loading, start, stop } = useLoading();
  const { isTeam, team } = useIsTeam();
  const { sendNotification } = usePushNotification();
  const { state, dispatch } = useStoreContext();
  const router = useRouter();
  const socket = useAbly();

  const log_data = useMemo<LogsData>(() => {
    return state.log as LogsData;
  }, [state.log]);

  const { projectId, id, title, type, logName, time: logTime } = log_data;

  const { messages, isLoading, conversationId, setMessages } = useChatMessages({
    selectedLog,
    projectId: "683f602cf0a10b4929a7a2aa",
    userId: "6383e95575784119b273bcf0",
  });

  const {
    logs = [],
    loading: logLoading,
    isPaused,
    togglePause,
    pendingLogsCount,
  } = useLogs(log_data as any);

  const channel = socket.channels.get(String(projectId));

  const [hasNewLogs, setHasNewLogs] = useState(false);

  const time = useMemo(() => {
    if (timer >= 3600) {
      return `${Math.floor(timer / 3600)} h`;
    } else if (timer >= 60) {
      return `${Math.floor(timer / 60)} m`;
    } else if (timer < 60) {
      return `${timer} s`;
    }
    return "";
  }, [timer]);

  const handleCloseLogModal = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }

      dispatch({
        type: Actions.LOGID,
        payload: {
          id: "",
          type: "",
          title: "",
          projectId: "",
          logName: "",
          time: {},
        },
      });

      setTimer(0);
      setTiming(false);
      setMinimized(false);
      setDeployed(false);
      setFailed(false);
      clearTimeout(timerout);

      if (router.query.logId) {
        const query = { ...router.query };
        delete query.logId;
        delete query.type;
        router.replace(
          {
            pathname: router.pathname,
            query,
          },
          undefined,
          { shallow: true },
        );
      }
    },
    [router, dispatch, id, projectId],
  );

  const handleMinizeModal = useCallback((e?: React.MouseEvent) => {
    setMinimized(true);
  }, []);

  const handleMaximizeModal = useCallback((e?: React.MouseEvent) => {
    setMinimized(false);
  }, []);

  const handleRedeploy = useCallback(
    (e?: React.MouseEvent) => {
      if (e) e.stopPropagation();

      start();
      redeployProject(projectId as any, {
        logId: id,
      })
        .then(() => {
          setFailed(false);
          setTiming(true);
          stop();
        })
        .catch(handleRequestError)
        .finally(() => stop());
    },
    [projectId, id, start, stop],
  );

  const handleScroll = useCallback(() => {
    if (!logContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
    const isScrolledToBottom = scrollHeight - scrollTop - clientHeight < 50;

    if (isScrolledToBottom) {
      setShouldAutoScroll(true);
    } else {
      setShouldAutoScroll(false);
    }

    lastScrollPosition.current = scrollTop;
  }, []);

  useEffect(() => {
    if (logs && logContainerRef.current) {
      if (shouldAutoScroll) {
        logContainerRef.current.scrollTop =
          logContainerRef.current.scrollHeight;
      } else if (logs?.length > lastLogLength.current) {
        setHasNewLogs(true);
      }

      lastLogLength.current = logs?.length || 0;
    }
  }, [logs, shouldAutoScroll]);

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (logContainerRef.current) {
        logContainerRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    if (logs?.length !== lastLogLength.current) {
      setHasNewLogs(true);
    }
  }, [logs?.length]);

  const scrollToBottom = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
      setShouldAutoScroll(true);
      setHasNewLogs(false);
    }
  }, []);

  const hasProcessedDeploy = useRef<boolean>(false);

  const onDeployed = useCallback(() => {
    if (hasProcessedDeploy.current) return;

    const isWebProject = router?.query?.serviceType === ServiceType.WebService;

    const handleDeploy = ({ data }: any) => {
      hasProcessedDeploy.current = true;

      channel.unsubscribe("deploy", handleDeploy);

      toast.success("Deployment successful");

      setTiming(false);

      sendNotification({
        title: "Deployment Successful",
        body: data.message,
        onClick: () => window.open(data.url, "_blank"),
      });

      setTimeout(() => {
        if ((!log_data.type || log_data?.type === "deploy") && isWebProject) {
          router.replace(
            `${isTeam ? `/teams/${team}` : ""}/projects/new/deployed?project=${
              log_data.projectId || data.id
            }`,
          );
        }

        handleCloseLogModal();
        setDeployed(true);
      }, 100);
    };

    channel.subscribe("deploy", handleDeploy);

    return () => {
      channel.unsubscribe("deploy", handleDeploy);
    };
  }, [
    router,
    channel,
    log_data,
    isTeam,
    team,
    sendNotification,
    handleCloseLogModal,
  ]);

  const onDeploymentFail = useCallback(() => {
    const handleDeployError = () => {
      if (hasProcessedDeploy.current || deployed) return;

      channel.unsubscribe("deploy-error");

      toast.error("Deployment failed", {
        description:
          "Quick tip: Try changing framework to 'other' in configuration tab",
      });
      setTiming(false);
      sendNotification({
        title: "Deployment Failed",
        body: `Deployment for ${logName} failed. Check logs for more details.`,
      });
      setFailed(true);
    };

    channel.subscribe("deploy-error", handleDeployError);

    return () => {
      channel.unsubscribe("deploy-error", handleDeployError);
    };
  }, [channel, deployed, logName, sendNotification]);

  useEffect(() => {
    if (timing && id) {
      timerout = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerout);
      setTimer(0);
    }

    return () => {
      clearInterval(timerout);
    };
  }, [timer, timing, id]);

  useEffect(() => {
    let deployCleanup: (() => void) | undefined;
    let errorCleanup: (() => void) | undefined;

    if (!isPaused && (id || projectId)) {
      hasProcessedDeploy.current = false;

      deployCleanup = onDeployed();
      errorCleanup = onDeploymentFail();
    }

    return () => {
      if (deployCleanup) deployCleanup();
      if (errorCleanup) errorCleanup();
    };
  }, [isPaused, id, projectId, onDeployed, onDeploymentFail]);

  useEffect(() => {
    if (id && logTime?.start && !logTime?.end) {
      setTiming(true);
    }
  }, [id, logTime]);

  const handleSendMessage = useCallback(
    async (e: React.FormEvent | string) => {
      let messageContent: string;

      if (typeof e !== "string") {
        e.preventDefault();
        messageContent = inputMessage.trim();
      } else {
        messageContent = e.trim();
      }

      if (
        !messageContent ||
        !selectedLog ||
        !conversationId ||
        !projectId ||
        !state.user?.id
      ) {
        return;
      }

      try {
        const response = await fetch("/api/chat/send-message", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            conversation_id: conversationId,
            role: LogMessageUser.User,
            content: messageContent,
            log_id: selectedLog.id,
            project_id: projectId,
            user_id: state.user.id,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Failed to send message via API",
          );
        }

        setInputMessage("");
      } catch (error: any) {
        console.error("Error sending message via API:", error);
        toast.error(error.message || "Failed to send message.");
      }
    },
    [inputMessage, selectedLog, conversationId, projectId, state.user?.id],
  );

  const handleLogClick = useCallback(
    async (log: LogEntry) => {
      setSelectedLog(log);
      //setShowAIChat(true);
      setInputMessage("");

      // Send the selected log as the first message
      await handleSendMessage(log.content);
    },
    [handleSendMessage],
  );

  useEffect(() => {
    if (chatContainerRef.current) {
      const scrollToBottom = () => {
        const container = chatContainerRef.current;
        if (container) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
          });
        }
      };

      const timeoutId = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modelDropdownRef.current &&
        !modelDropdownRef.current.contains(event.target as Node)
      ) {
        setIsModelDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!id) return null;

  if (minimized) {
    return (
      <ModalHeaderContent
        minimized={minimized}
        title={title || ""}
        timer={timer}
        logName={logName || ""}
        time={time || ""}
        isPaused={isPaused}
        pendingLogsCount={pendingLogsCount}
        togglePause={togglePause}
        handleCloseLogModal={handleCloseLogModal}
        handleMinizeModal={handleMinizeModal}
        handleMaximizeModal={handleMaximizeModal}
        type={type || ""}
        failed={failed}
        copyEnabled={false}
        pauseEnabled={false}
        minMaxEnabled={false}
      />
    );
  }

  return (
    <Modal open={!!id} onClose={handleCloseLogModal} verticalOffset={50}>
      <ModalContent
        ref={modalRef}
        className="space-y-4 bg-white dark:bg-theme-color"
        style={{
          width: "90vw",
          maxWidth: "90vw",
          height: "100vh",
          maxHeight: "100vh",
          margin: 0,
          overflowY: "auto",
          position: "relative",
          marginTop: "-5%",
          padding: "16px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeaderContent
          minimized={minimized}
          title={title || ""}
          timer={timer}
          logName={logName || ""}
          time={time || ""}
          isPaused={isPaused}
          pendingLogsCount={pendingLogsCount}
          togglePause={togglePause}
          handleCloseLogModal={handleCloseLogModal}
          handleMinizeModal={handleMinizeModal}
          handleMaximizeModal={handleMaximizeModal}
          type={type || ""}
          failed={failed}
          copyEnabled={true}
          pauseEnabled={true}
          minMaxEnabled={true}
        />

        <div className="flex gap-4" style={{ height: "calc(100vh - 90px)" }}>
          <div
            ref={logContainerRef}
            className={`custom-scrollbar-v relative flex flex-1 flex-col overflow-y-auto scroll-smooth rounded-none bg-black font-jetbrains text-sm font-semibold text-[#666] dark:bg-gray-900 dark:text-theme-color-lighter ${
              showAIChat ? "w-2/3" : "w-full"
            }`}
          >
            {logLoading ? (
              <div className="flex h-full w-full flex-col items-center space-y-2 p-5">
                {Array.from({ length: 30 }, (_, i) => i + 1).map((i) => (
                  <div
                    className="flex w-full items-center justify-between space-x-4"
                    key={i}
                  >
                    <Skeleton className="h-5 w-1/4 rounded-full dark:!bg-theme-color" />
                    <Skeleton className="h-5 w-3/4 rounded-full dark:!bg-theme-color" />
                  </div>
                ))}
              </div>
            ) : logs.length === 0 && !logLoading ? (
              <div className="flex h-full w-full flex-col items-center space-y-2 p-5">
                <div className="relative h-2/4 w-4/5">
                  <Image
                    alt="no logs"
                    src="https://res.cloudinary.com/dgqfojhx4/image/upload/v1708776908/dashboard-assets/no-logs_zi9gs1.svg"
                    layout="fill"
                    className="object-contain"
                  />
                </div>
                <p className="text-center text-lg font-extrabold">
                  No Logs Found!
                </p>
                <p className="w-1/2 text-center text-gray-500">
                  This deployment {logName ? `(${logName})` : ""} currently has
                  no logs. Try Re-deploying this history deployment to see logs.
                </p>
                <Button
                  loading={!!loading}
                  onClick={handleRedeploy}
                  className="text-white"
                >
                  Redeploy
                </Button>
              </div>
            ) : (
              <>
                {logs.map((log: LogEntry, index: number) => {
                  const [date, logContent] = log.content.split("---");
                  if (!date || !logContent?.trim()) return null;
                  return (
                    <div
                      key={index}
                      className={`w-full cursor-pointer rounded-md p-3 text-[14px] text-white transition-colors hover:bg-gray-800 ${
                        selectedLog === log ? "bg-gray-800" : ""
                      }`}
                      onClick={() => handleLogClick(log)}
                    >
                      <span
                        className={`${log_class(date)} font-mono font-thin`}
                      >
                        {formatDate(date.trim())}{" "}
                      </span>
                      <span
                        className={`${log_class(
                          logContent,
                        )} whitespace-pre-wrap font-medium`}
                      >
                        {convertUrlsToLinks(logContent.trim())}
                      </span>
                    </div>
                  );
                })}

                {hasNewLogs && (
                  <div className="sticky bottom-4 w-full">
                    <div className="flex justify-end pr-4">
                      <IconButton
                        title="Jump to latest logs"
                        className="btn-md border-none bg-gray-500 text-white shadow-md hover:bg-gray-600"
                        onClick={scrollToBottom}
                      >
                        <ArrowDown size={15} />
                      </IconButton>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {showAIChat && (
            <div className="flex w-1/3 flex-col bg-white p-4 shadow-sm dark:bg-theme-color">
              <div className="mb-4 flex items-center justify-between border-b border-dashed dark:border-gray-500">
                <div className="flex items-center gap-2">
                  <h3 className="text-md font-mono font-semibold">Nyph</h3>
                  <div className="relative" ref={modelDropdownRef}>
                    <button
                      onClick={() =>
                        setIsModelDropdownOpen(!isModelDropdownOpen)
                      }
                      className="flex items-center gap-1 text-xs text-gray-600 transition-colors hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
                    >
                      {selectedModel.name}
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${
                          isModelDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isModelDropdownOpen && (
                      <div className="absolute left-0 top-full z-10 mt-1 w-48 bg-white shadow-sm dark:bg-theme-color">
                        {AI_MODELS.map((model) => (
                          <button
                            key={model.id}
                            onClick={() => {
                              setSelectedModel(model);
                              setIsModelDropdownOpen(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-xs transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${
                              selectedModel.id === model.id
                                ? "bg-gray-50 dark:bg-gray-700/50"
                                : ""
                            }`}
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {model.name}
                            </div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400">
                              {model.description}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="text-gray-600 transition-colors hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
                    title="Configure AI settings"
                  >
                    <Settings2 size={14} />
                  </button>
                </div>
                <IconButton
                  title="Close AI chat"
                  className="border-none bg-transparent text-gray-700 hover:bg-transparent dark:text-gray-200"
                  onClick={() => {
                    setShowAIChat(false);
                    setSelectedLog(null);
                    setMessages([]);
                    setInputMessage("");
                  }}
                >
                  <MdClose size={20} />
                </IconButton>
              </div>

              <div
                className={`mb-3 overflow-hidden transition-all duration-300 ease-in-out ${
                  showSettings
                    ? "max-h-[200px] opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="transform bg-gray-50 p-3 transition-all duration-300 dark:bg-theme-color-light">
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                        Max Tokens: {config.maxTokens}
                      </label>
                      <input
                        type="range"
                        min="100"
                        max="4000"
                        step="100"
                        value={config.maxTokens}
                        onChange={(e) =>
                          setConfig((prev) => ({
                            ...prev,
                            maxTokens: parseInt(e.target.value),
                          }))
                        }
                        className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 dark:bg-gray-700"
                      />
                      <div className="mt-1 flex justify-between text-[10px] text-gray-500 dark:text-gray-400">
                        <span>100</span>
                        <span>4000</span>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                        Temperature: {config.temperature.toFixed(1)}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={config.temperature}
                        onChange={(e) =>
                          setConfig((prev) => ({
                            ...prev,
                            temperature: parseFloat(e.target.value),
                          }))
                        }
                        className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 dark:bg-gray-700"
                      />
                      <div className="mt-1 flex justify-between text-[10px] text-gray-500 dark:text-gray-400">
                        <span>0.0</span>
                        <span>2.0</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="custom-scrollbar-v mb-4 flex-1 overflow-y-auto"
                ref={chatContainerRef}
              >
                {!hasSeenDisclaimer && (
                  <div className="mb-4 border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500" />
                      <div>
                        <h4 className="mb-1 text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          LLM Tokens Required
                        </h4>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300">
                          To use Nyph, you need to have LLM tokens purchased on
                          Brimble. Each interaction will consume tokens from
                          your balance.
                        </p>
                        <button
                          onClick={() => setHasSeenDisclaimer(true)}
                          className="mt-2 text-xs font-medium text-yellow-800 hover:text-yellow-900 dark:text-yellow-200 dark:hover:text-yellow-100"
                        >
                          I understand
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {hasSeenDisclaimer && (
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.role === LogMessageUser.User
                            ? "justify-end"
                            : "justify-start"
                        } animate-fade-in`}
                      >
                        <div
                          className={`max-w-[80%] transform p-3 transition-all duration-300 ease-out ${
                            message.role === LogMessageUser.User
                              ? "translate-x-0 bg-primary text-white"
                              : message.role === LogMessageUser.Assistant
                                ? "translate-x-0 bg-light text-gray-800 dark:bg-theme-color-light dark:text-gray-200"
                                : "translate-x-0 bg-light text-gray-800 dark:bg-theme-color-light dark:text-gray-200"
                          }`}
                        >
                          <p className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-xs">
                            {message.isTemporaryLoading ? (
                              <span className="flex items-center gap-2">
                                {message.content}
                                <span className="flex gap-1">
                                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s] dark:bg-gray-500" />
                                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s] dark:bg-gray-500" />
                                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 dark:bg-gray-500" />
                                </span>
                              </span>
                            ) : (
                              message.content
                            )}
                          </p>
                          {message.role !== LogMessageUser.Assistant && (
                            <span className="mt-1 block text-xs opacity-70">
                              {format(new Date(message?.created_at), "Pp")}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <form
                onSubmit={handleSendMessage}
                className="flex gap-2 transition-all duration-300 ease-in-out"
              >
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Ask about this log..."
                  className="flex-1 border-gray-300 bg-light px-3 py-2 transition-all duration-300 focus:outline-none dark:bg-theme-color-light dark:text-white"
                  disabled={!hasSeenDisclaimer || isLoading}
                />
                <IconButton
                  type="submit"
                  title="Send message"
                  className="btn-md transform border-none bg-primary text-white transition-all duration-300 hover:scale-105 hover:bg-blue-600 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!hasSeenDisclaimer || isLoading}
                >
                  <Send size={20} />
                </IconButton>
              </form>
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default LogsModal;
